import { trimZones } from "@/components/explore/ExploreView";
import { findClosestTimeIndex } from "@/components/explore/Heatmap";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, ExploreDisplay } from "@/pages/data/DataChart";
import { getZoneData } from "@/queries/report";
import { useReportContext } from "@/stores/ReportContext";
import { useSessionData } from "@/stores/SessionDataContext";
import { useCallback, useMemo, useRef, useState } from "react";
import ReactFlow, {
  Background,
  BaseEdge,
  Edge,
  EdgeProps,
  getStraightPath,
  MarkerType,
  Node,
  Panel,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import { APMLegend } from "./APMLegend";
import { StationByTrackView } from "./StationByTrackView";
import { TrainPOVTable } from "./TrainPOVTable";

const edgeAnimationStyle = `
  @keyframes flowAnimation {
    0% {
      stroke-dashoffset: 24;
    }
    100% {
      stroke-dashoffset: 0;
    }
  }
  
  @keyframes flowAnimationReverse {
    0% {
      stroke-dashoffset: 0;
    }
    100% {
      stroke-dashoffset: 24;
    }
  }

  .react-flow__node:not([data-id^="label-"]) {
    transition: all 0.2s ease;
    cursor: pointer !important;
  }

  .react-flow__node:not([data-id^="label-"]):hover {
    filter: brightness(1.1);
    transform: scale(1.05);
    box-shadow: 0 0 20px rgba(96, 165, 250, 0.1);
  }

  .react-flow__edge {
    cursor: pointer !important;
  }

  .react-flow__edge path {
    transition: all 0.2s ease;
  }

  .react-flow__edge:hover path:not(.react-flow__edge-interaction) {
    stroke-width: 4 !important;
    filter: brightness(1.4) drop-shadow(0 0 8px rgba(96, 165, 250, 0.1));
  }
`;

interface Station {
  id: string;
  code: string;
  name: string;
  type: "terminal" | "concourse";
  airside: boolean;
}

interface Track {
  id: string;
  code: string;
  type: "loop" | "shuttle";
  route: string[];
}

interface APMSubwayMapProps {
  stations: Station[];
  tracks: Track[];
}

const createSubwayNodesAndEdges = (
  stations: Station[],
  tracks: Track[],
  selectedStationId?: string,
  selectedTrackId?: string,
  selectedOriginStationId?: string,
  selectedDestinationStationId?: string
): { nodes: Node[]; edges: Edge[] } => {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  const spacing = 200;
  const startX = 150;
  const singleLineY = 100;
  const edgeOffset = 15;

  // Create unique station nodes only once
  const uniqueStationIds = Array.from(new Set(tracks.flatMap((t) => t.route)));
  const stationPositionMap = new Map<string, number>();

  // Add vertical background rectangles for individual airside stations
  uniqueStationIds.forEach((stationId, index) => {
    const station = stations.find((s) => s.id === stationId);
    if (!station || !station.airside) return;

    const nodeWidth = 100;
    const padding = 0;
    const rectWidth = nodeWidth + padding * 2;
    const rectHeight = 300;
    const x = startX + index * spacing - padding;
    const y = singleLineY - rectHeight / 2 + 30;

    nodes.push({
      id: `concourse-bg-${stationId}`,
      type: "concourseBg",
      position: { x, y },
      data: {
        width: rectWidth,
        height: rectHeight,
      },
      style: {
        pointerEvents: "none",
        zIndex: -1,
      },
      draggable: false,
      selectable: false,
    });
  });

  uniqueStationIds.forEach((stationId, index) => {
    const station = stations.find((s) => s.id === stationId);
    if (!station) return;

    const x = startX + index * spacing;
    stationPositionMap.set(stationId, x);
    const isStationSelected = selectedStationId === stationId;

    nodes.push({
      id: stationId,
      type: "default",
      position: { x, y: singleLineY },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      data: {
        label: (
          <div className="text-center px-2">
            <div className="font-semibold text-xs">{station.code}</div>
            <div className="text-[10px] text-slate-400 whitespace-nowrap mt-0.5">
              {station.name}
            </div>
          </div>
        ),
        stationId: station.id,
        stationCode: station.code,
      },
      style: {
        background: isStationSelected
          ? "#1e40af"
          : station.airside
            ? "#0f172a"
            : "#334155",
        color: "#e2e8f0",
        border: `3px solid ${isStationSelected ? "#60a5fa" : station.airside ? "#3b82f6" : "#64748b"}`,
        borderRadius: "8px",
        width: 100,
        height: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "11px",
        fontWeight: "500",
        padding: "4px",
        cursor: "pointer",
        zIndex: 1,
      },
    });
  });

  // Create edges with offsets for each track
  tracks.forEach((track, trackIndex) => {
    const yOffset = trackIndex === 0 ? -edgeOffset : edgeOffset;

    track.route.forEach((stationId, routeIndex) => {
      if (routeIndex < track.route.length - 1) {
        const targetStationId = track.route[routeIndex + 1];
        const isSegmentSelected =
          selectedTrackId === track.id &&
          selectedOriginStationId === stationId &&
          selectedDestinationStationId === targetStationId;

        edges.push({
          id: `${track.id}-${stationId}-${targetStationId}`,
          source: stationId,
          target: targetStationId,
          type: "offsetEdge",
          animated: false,
          data: {
            trackId: track.id,
            trackCode: track.code,
            originStationId: stationId,
            destinationStationId: targetStationId,
            yOffset: yOffset,
            direction: trackIndex === 0 ? "forward" : "reverse",
          },
          style: {
            stroke: isSegmentSelected ? "#60a5fa" : "#64748b",
            strokeWidth: isSegmentSelected ? 3 : 2,
            cursor: "pointer",
            strokeDasharray: "2 6",
            animation: "flowAnimation 1s linear infinite",
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: isSegmentSelected ? "#60a5fa" : "#64748b",
          },
        });
      }

      if (track.type === "loop" && routeIndex === track.route.length - 1) {
        const firstStationId = track.route[0];
        const isLoopSegmentSelected =
          selectedTrackId === track.id &&
          selectedOriginStationId === stationId &&
          selectedDestinationStationId === firstStationId;

        edges.push({
          id: `${track.id}-${stationId}-${firstStationId}-loop`,
          source: stationId,
          target: firstStationId,
          type: "offsetLoopback",
          animated: false,
          data: {
            trackId: track.id,
            trackCode: track.code,
            originStationId: stationId,
            destinationStationId: firstStationId,
            yOffset: yOffset,
            curveAbove: trackIndex === 0,
            direction: trackIndex === 0 ? "forward" : "reverse",
          },
          style: {
            stroke: isLoopSegmentSelected ? "#60a5fa" : "#64748b",
            strokeWidth: isLoopSegmentSelected ? 3 : 2,
            cursor: "pointer",
            strokeDasharray: "2 6",
            animation: "flowAnimation 1s linear infinite",
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: isLoopSegmentSelected ? "#60a5fa" : "#64748b",
          },
        });
      }
    });
  });

  return { nodes, edges };
};

const OffsetEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  data,
  style = {},
  markerEnd,
}: EdgeProps) => {
  const yOffset = data?.yOffset || 0;
  const adjustedSourceY = sourceY + yOffset;
  const adjustedTargetY = targetY + yOffset;

  const [edgePath] = getStraightPath({
    sourceX: sourceX,
    sourceY: adjustedSourceY,
    targetX: data.direction === "forward" ? targetX : targetX + 100,
    targetY: adjustedTargetY,
  });

  return (
    <>
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        className="react-flow__edge-interaction"
      />
      <BaseEdge id={id} path={edgePath} style={style} markerEnd={markerEnd} />
    </>
  );
};

const OffsetLoopbackEdge = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  style = {},
  markerEnd,
  data,
}: EdgeProps) => {
  const yOffset = data?.yOffset || 0;
  const adjustedSourceY = sourceY + yOffset;
  const adjustedTargetY = targetY + yOffset;

  const nodeWidth = 100;
  const adjustedSourceX =
    data?.direction === "forward" ? sourceX : sourceX - nodeWidth;
  const adjustedTargetX =
    data?.direction === "forward" ? targetX : targetX + nodeWidth;

  const curveOffset = data?.curveAbove ? -75 : 75;
  const centerX = (adjustedSourceX + adjustedTargetX) / 2;
  const centerY = (adjustedSourceY + adjustedTargetY) / 2 + curveOffset;

  const controlPointOffset = data?.direction === "forward" ? 100 : -100;
  const customPath = `M ${adjustedSourceX},${adjustedSourceY} Q ${adjustedSourceX + controlPointOffset},${adjustedSourceY + curveOffset} ${centerX},${centerY} T ${adjustedTargetX},${adjustedTargetY}`;

  return (
    <>
      <path
        d={customPath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        className="react-flow__edge-interaction"
      />
      <BaseEdge id={id} path={customPath} style={style} markerEnd={markerEnd} />
    </>
  );
};

const TextNode = ({ data }) => {
  return <div className="text-node">{data.label}</div>;
};

const ConcourseBgNode = ({ data }) => {
  return (
    <div
      style={{
        width: data.width,
        height: data.height,
        background: "rgba(15, 23, 42, 0.4)",
        border: "2px solid rgba(59, 130, 246, 0.3)",
        borderRadius: "12px",
        pointerEvents: "none",
      }}
    />
  );
};

export function APMSubwayMap({ stations, tracks }: APMSubwayMapProps) {
  const sessionData = useSessionData();
  const customPrefix = sessionData.airport.apm?.custom_prefix || "APM";

  const [selectedStationId, setSelectedStationId] = useState<string>();
  const [selectedStationCode, setSelectedStationCode] = useState<string>();
  const [selectedTrackId, setSelectedTrackId] = useState<string>();
  const [selectedTrackCode, setSelectedTrackCode] = useState<string>();
  const [selectedOriginStationId, setSelectedOriginStationId] =
    useState<string>();
  const [selectedDestinationStationId, setSelectedDestinationStationId] =
    useState<string>();
  const [viewMode, setViewMode] = useState<"map" | "train-pov">("map");

  const [tooltip, setTooltip] = useState<{
    type: "station" | "edge";
    id?: string;
    code?: string;
    name?: string;
    trackCode?: string;
    originCode?: string;
    destCode?: string;
    x: number;
    y: number;
  } | null>(null);

  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { reportId } = useReportContext();
  const { airport_config } = useSessionData();

  const { data: stationData } = getZoneData(
    { type: "apm_station", measure: "pax" },
    reportId,
    true
  );

  const { data: trackData } = getZoneData(
    { type: "apm_track", measure: "pax", disable_grouping: true },
    reportId,
    true
  );

  const { times, zones: allZones } = useMemo(() => {
    const stationZones = stationData?.zones || [];
    const trackZones = trackData?.zones || [];
    const combinedZones = [...stationZones, ...trackZones];

    return trimZones(
      combinedZones,
      airport_config.start_hour,
      airport_config.end_hour,
      airport_config.interval_size
    );
  }, [stationData, trackData, airport_config]);

  const currentIntervalIndex = useMemo(
    () =>
      times.length > 0
        ? findClosestTimeIndex(times, airport_config.timezone)
        : 0,
    [times, airport_config.timezone]
  );

  const liveDataLookup = useMemo(() => {
    const lookup: {
      stations: Record<string, { current: number; next: number }>;
      segments: Record<string, { current: number; next: number }>;
    } = {
      stations: {},
      segments: {},
    };

    allZones.forEach((zone) => {
      const currentValue =
        currentIntervalIndex < zone.values.length
          ? zone.values[currentIntervalIndex]
          : 0;
      const nextValue =
        currentIntervalIndex + 1 < zone.values.length
          ? zone.values[currentIntervalIndex + 1]
          : 0;

      if (zone.apm_station_id) {
        const id = zone.apm_station_id.toString();
        lookup.stations[id] = {
          current: currentValue,
          next: nextValue,
        };
      } else if (
        zone.apm_track_id &&
        zone.apm_origin_station_id &&
        zone.apm_destination_station_id
      ) {
        const key = `${zone.apm_track_id}-${zone.apm_origin_station_id}-${zone.apm_destination_station_id}`;
        if (!lookup.segments[key]) {
          lookup.segments[key] = { current: 0, next: 0 };
        }
        lookup.segments[key].current += currentValue;
        lookup.segments[key].next += nextValue;
      }
    });

    return lookup;
  }, [allZones, currentIntervalIndex]);

  const { nodes, edges } = useMemo(
    () =>
      createSubwayNodesAndEdges(
        stations,
        tracks,
        selectedStationId,
        selectedTrackId,
        selectedOriginStationId,
        selectedDestinationStationId
      ),
    [
      stations,
      tracks,
      selectedStationId,
      selectedTrackId,
      selectedOriginStationId,
      selectedDestinationStationId,
    ]
  );

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    if (!node.id.startsWith("label-") && node.data.stationId) {
      setSelectedStationId(node.data.stationId);
      setSelectedStationCode(node.data.stationCode);
      setSelectedTrackId(undefined);
      setSelectedTrackCode(undefined);
      setSelectedOriginStationId(undefined);
      setSelectedDestinationStationId(undefined);
    }
  }, []);

  const onEdgeClick = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      if (edge.data?.trackId) {
        setSelectedTrackId(edge.data.trackId);
        setSelectedTrackCode(edge.data.trackCode);
        setSelectedOriginStationId(edge.data.originStationId);
        setSelectedDestinationStationId(edge.data.destinationStationId);
        setSelectedStationId(undefined);
        setSelectedStationCode(undefined);
      }
    },
    [stations]
  );

  const onNodeMouseEnter = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      if (!node.id.startsWith("label-") && node.data.stationId) {
        const station = stations.find((s) => s.id === node.data.stationId);
        setTooltip((prev) => {
          if (prev?.type === "station" && prev?.id === node.data.stationId)
            return prev;
          return {
            type: "station",
            id: node.data.stationId,
            code: node.data.stationCode,
            name: station?.name,
            x: event.clientX,
            y: event.clientY,
          };
        });
      }
    },
    [stations]
  );

  const onNodeMouseLeave = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = setTimeout(() => {
      setTooltip(null);
    }, 100);
  }, []);

  const onEdgeMouseEnter = useCallback(
    (event: React.MouseEvent, edge: Edge) => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      if (edge.data?.trackId) {
        const originStation = stations.find(
          (s) => s.id === edge.data.originStationId
        );
        const destStation = stations.find(
          (s) => s.id === edge.data.destinationStationId
        );

        setTooltip((prev) => {
          if (
            prev?.type === "edge" &&
            prev?.trackCode === edge.data.trackCode &&
            prev?.originCode === originStation?.code &&
            prev?.destCode === destStation?.code
          )
            return prev;
          return {
            type: "edge",
            trackCode: edge.data.trackCode,
            originCode: originStation?.code,
            destCode: destStation?.code,
            x: event.clientX,
            y: event.clientY,
          };
        });
      }
    },
    [stations]
  );

  const onEdgeMouseLeave = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = setTimeout(() => {
      setTooltip(null);
    }, 0);
  }, []);

  return (
    <div className="w-full grid grid-cols-4 space-y-2">
      <style>{edgeAnimationStyle}</style>

      <div className="col-span-4">
        <Tabs
          value={viewMode}
          onValueChange={(v) => setViewMode(v as "map" | "train-pov")}
        >
          <TabsList>
            <TabsTrigger value="map">Map View</TabsTrigger>
            <TabsTrigger value="train-pov">Train POV</TabsTrigger>
          </TabsList>

          <TabsContent value="map">
            <div className="w-full h-[500px] bg-slate-800 rounded border border-slate-700">
              <ReactFlow
                nodeTypes={{ text: TextNode, concourseBg: ConcourseBgNode }}
                edgeTypes={{
                  offsetEdge: OffsetEdge,
                  offsetLoopback: OffsetLoopbackEdge,
                }}
                nodes={nodes}
                edges={edges}
                onNodeClick={onNodeClick}
                onEdgeClick={onEdgeClick}
                onNodeMouseEnter={onNodeMouseEnter}
                onNodeMouseLeave={onNodeMouseLeave}
                onEdgeMouseEnter={onEdgeMouseEnter}
                onEdgeMouseLeave={onEdgeMouseLeave}
                fitView
                fitViewOptions={{ padding: 0.2, minZoom: 0.5, maxZoom: 1.5 }}
                proOptions={{ hideAttribution: true }}
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable={true}
                zoomOnScroll={false}
                zoomOnDoubleClick={false}
                panOnScroll={false}
                preventScrolling={false}
                minZoom={0.5}
                maxZoom={1.5}
              >
                <Background color="#475569" gap={20} className="bg-slate-800" />
                <Panel position="top-right" className="m-2">
                  <APMLegend
                    trackZones={trackData?.zones || []}
                    tracks={tracks}
                    currentIntervalIndex={currentIntervalIndex}
                    times={times}
                  />
                </Panel>
              </ReactFlow>
            </div>
          </TabsContent>

          <TabsContent value="train-pov">
            <TrainPOVTable
              zones={trackData?.zones || []}
              tracks={tracks}
              stations={stations}
              times={times}
              currentIntervalIndex={currentIntervalIndex}
            />
          </TabsContent>
        </Tabs>
      </div>

      {tooltip && tooltip.type === "station" && (
        <div
          className="fixed bg-slate-800 border-2 border-slate-600 rounded-lg px-3 py-2 text-sm pointer-events-none z-50 shadow-xl max-w-xs"
          style={{ left: tooltip.x + 15, top: tooltip.y + 15 }}
        >
          <div className="font-semibold text-blue-400 mb-1">{tooltip.code}</div>
          <div className="text-slate-300 text-xs mb-1">{tooltip.name}</div>
          <div className="text-white font-medium">
            Current: {liveDataLookup.stations[tooltip.id!]?.current || 0} pax
          </div>
          <div className="text-slate-400 text-xs mt-1 mb-2">
            Next: {liveDataLookup.stations[tooltip.id!]?.next || 0} pax
          </div>

          {/* Outbound segments */}
          {(() => {
            const outboundSegments = tracks.flatMap((track) =>
              track.route
                .map((stationId, idx) => {
                  if (
                    stationId === tooltip.id &&
                    idx < track.route.length - 1
                  ) {
                    const destStationId = track.route[idx + 1];
                    const destStation = stations.find(
                      (s) => s.id === destStationId
                    );
                    const segmentKey = `${track.id}-${stationId}-${destStationId}`;
                    const segmentData = liveDataLookup.segments[segmentKey];
                    return {
                      trackCode: track.code,
                      destCode: destStation?.code,
                      data: segmentData,
                    };
                  }
                  // Check loop connections
                  if (
                    track.type === "loop" &&
                    stationId === tooltip.id &&
                    idx === track.route.length - 1
                  ) {
                    const destStationId = track.route[0];
                    const destStation = stations.find(
                      (s) => s.id === destStationId
                    );
                    const segmentKey = `${track.id}-${stationId}-${destStationId}`;
                    const segmentData = liveDataLookup.segments[segmentKey];
                    return {
                      trackCode: track.code,
                      destCode: destStation?.code,
                      data: segmentData,
                    };
                  }
                  return null;
                })
                .filter(Boolean)
            );

            if (outboundSegments.length > 0) {
              return (
                <div className="border-t border-slate-600 pt-2 mt-2">
                  <div className="text-slate-400 text-xs font-semibold mb-1">
                    Outbound Segments:
                  </div>
                  {outboundSegments.map((segment, idx) => (
                    <div key={idx} className="text-xs mb-1">
                      <div className="text-slate-300">
                        {tooltip.code} → {segment.destCode} ({segment.trackCode}
                        )
                      </div>
                      <div className="text-slate-400 text-[10px] ml-2">
                        Current: {segment.data?.current || 0} | Next:{" "}
                        {segment.data?.next || 0}
                      </div>
                    </div>
                  ))}
                </div>
              );
            }
            return null;
          })()}
        </div>
      )}

      {tooltip && tooltip.type === "edge" && (
        <div
          className="fixed bg-slate-800 border-2 border-slate-600 rounded-lg px-3 py-2 text-sm pointer-events-none z-50 shadow-xl"
          style={{ left: tooltip.x + 15, top: tooltip.y + 15 }}
        >
          <div className="font-semibold text-blue-400 mb-1">
            {tooltip.trackCode} Track
          </div>
          <div className="text-slate-300 text-xs mb-1">
            {tooltip.originCode} → {tooltip.destCode}
          </div>
          <div className="text-white font-medium">
            Current:{" "}
            {liveDataLookup.segments[
              `${tracks.find((t) => t.code === tooltip.trackCode)?.id}-${stations.find((s) => s.code === tooltip.originCode)?.id}-${stations.find((s) => s.code === tooltip.destCode)?.id}`
            ]?.current || 0}{" "}
            pax
          </div>
          <div className="text-slate-400 text-xs mt-1">
            Next:{" "}
            {liveDataLookup.segments[
              `${tracks.find((t) => t.code === tooltip.trackCode)?.id}-${stations.find((s) => s.code === tooltip.originCode)?.id}-${stations.find((s) => s.code === tooltip.destCode)?.id}`
            ]?.next || 0}{" "}
            pax
          </div>
        </div>
      )}

      {(selectedStationId || selectedTrackId) && (
        <div className="col-span-4">
          <Button
            onClick={() => {
              setSelectedStationId(undefined);
              setSelectedStationCode(undefined);
              setSelectedTrackId(undefined);
              setSelectedTrackCode(undefined);
              setSelectedOriginStationId(undefined);
              setSelectedDestinationStationId(undefined);
            }}
            variant="outline"
            size="sm"
          >
            ← Back to Overview
          </Button>
        </div>
      )}

      {selectedStationId && (
        <div className="col-span-4">
          <Card title={`Station: ${selectedStationCode}`}>
            <StationByTrackView
              trackZones={trackData?.zones || []}
              stations={stations.filter((s) => s.id === selectedStationId)}
              tracks={tracks}
              times={times}
            />
          </Card>
        </div>
      )}

      {selectedTrackId && (
        <div className="col-span-4">
          <Card
            title={`Track: ${selectedTrackCode}${
              selectedOriginStationId && selectedDestinationStationId
                ? ` (${stations.find((s) => s.id === selectedOriginStationId)?.code} → ${stations.find((s) => s.id === selectedDestinationStationId)?.code})`
                : ""
            }`}
          >
            <ExploreDisplay
              height={600}
              query={{
                type: "apm_track",
                measure: "pax",
                apm_track_id: parseInt(selectedTrackId),
                apm_origin_station_id: selectedOriginStationId
                  ? parseInt(selectedOriginStationId)
                  : undefined,
                apm_destination_station_id: selectedDestinationStationId
                  ? parseInt(selectedDestinationStationId)
                  : undefined,
              }}
            />
          </Card>
        </div>
      )}

      {!selectedStationId && !selectedTrackId && viewMode === "map" && (
        <div className="col-span-4">
          <Card title={`${customPrefix} Overview`}>
            <StationByTrackView
              trackZones={trackData?.zones || []}
              stations={stations}
              tracks={tracks}
              times={times}
            />
          </Card>
        </div>
      )}
    </div>
  );
}
